import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import merge from 'lodash/merge';
import get from 'lodash/get';

import { Nullable } from '@tager/web-core';

import CircularCheckbox from '@/components/CircularCheckbox';
import useLocalStorage from '@/hooks/useLocalStorage';
import { submitQuiz } from '@/services/requests';
import { VoteOption } from '@/typings/model';
import { media } from '@/utils/mixin';

import Progress from './components/Progress';

interface Props {
  id: number;
  question: Nullable<string>;
  answers: VoteOption[];
}

function Polls({ id, question, answers }: Props) {
  const router = useRouter();
  const slug = (router.query.slug as string | undefined) ?? '/';

  const [polls, setPolls] = useLocalStorage<{
    [p: string]: { [p: string]: number };
  }>(`reviews/:slug/polls`, {});

  const [selectedAnswerId, setSelectedAnswerId] = useState<number | null>(
    get(polls, [id, slug], null)
  );
  const totalVotes = useMemo(
    () => answers.reduce((total, answer) => total + answer.value, 0),
    [answers]
  );

  const [answeredId, setAnsweredId] = useState<Nullable<number>>(null);

  function calculatePercent(votes: number, total: number): number {
    let totalResult = total;

    if (answeredId) {
      totalResult += 1;
    }

    if (votes === 0) {
      return 0;
    }

    return Math.round((votes / totalResult) * 100);
  }

  function handleSubmitVote(answerId: number) {
    setAnsweredId(answerId);
    submitQuiz(id, answerId)
      .then((response) => {
        setSelectedAnswerId(answerId);
        setPolls(merge(polls, { [id]: { [slug]: answerId } }));
      })
      .catch(console.error);
  }

  function handleChangeAnswer(answerId: number) {
    return () => {
      if (selectedAnswerId) return;
      handleSubmitVote(answerId);
    };
  }

  function isSelectedAnswer(answerId: number): boolean {
    return selectedAnswerId === answerId;
  }

  return (
    <Component>
      <ComponentContainer>
        <QuestionContainer>
          <Question>{question}</Question>
        </QuestionContainer>
        <AnswerList length={answers.length > 4 ? 4 : answers.length}>
          <>
            {answers.map((answer, index) => (
              <AnswerItem isVoted={Boolean(selectedAnswerId)} key={index + 1}>
                <AnswerHeader>
                  <AnswerLabel>
                    <AnswerCheckbox
                      checked={isSelectedAnswer(index + 1)}
                      onChange={handleChangeAnswer(index + 1)}
                    />
                    <AnswerLabelText>{answer.option}</AnswerLabelText>
                  </AnswerLabel>
                  {selectedAnswerId && (
                    <AnswerPercent>
                      {answeredId === index + 1
                        ? calculatePercent(answer.value + 1, totalVotes)
                        : calculatePercent(answer.value, totalVotes)}
                      {'%'}
                    </AnswerPercent>
                  )}
                </AnswerHeader>
                {selectedAnswerId && (
                  <StyledProgress
                    percent={
                      answeredId === index + 1
                        ? calculatePercent(answer.value + 1, totalVotes)
                        : calculatePercent(answer.value, totalVotes)
                    }
                  />
                )}
              </AnswerItem>
            ))}
          </>
        </AnswerList>
      </ComponentContainer>
    </Component>
  );
}

export default Polls;

const Component = styled.div`
  width: 100%;
  max-width: 67%;

  ${media.tablet(
    css`
      max-width: 100%;
    `
  )}

  margin: 0 auto;
  padding: 50px 20px 0 20px;
`;

const ComponentContainer = styled.div`
  padding: 40px 20px;

  background: ${(props) => props.theme.gray_1};

  @media (min-width: 1400px) {
    padding: 50px 62px;
  }
`;

const QuestionContainer = styled.div`
  margin-bottom: 25px;

  @media (min-width: 1400px) {
    margin-bottom: 30px;
  }
`;

const Question = styled.span`
  font-weight: 700;
  font-size: 20px;
  line-height: 33px;
  color: ${(props) => props.theme.black100};

  @media (min-width: 1400px) {
    font-size: 28px;
    line-height: 56px;
  }
`;

const AnswerList = styled.ul<{
  isImage?: boolean;
  length: number;
}>`
  @media (min-width: 576px) {
    ${({ isImage, length }) =>
      isImage &&
      length >= 2 &&
      css`
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        column-gap: 20px;

        > * {
          padding: 20px 0 15px 0 !important;
        }
      `};

    @media (min-width: 992px) {
      ${({ isImage, length }) =>
        isImage &&
        length >= 2 &&
        css`
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          column-gap: 20px;

          > * {
            padding: 20px 0 15px 0 !important;
          }
        `};

      ${({ isImage, length }) =>
        isImage &&
        length >= 3 &&
        css`
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          column-gap: 20px;

          > * {
            padding: 20px 0 15px 0 !important;
          }
        `};
    }
  }

  @media (min-width: 1400px) {
    ${({ isImage, length }) =>
      isImage &&
      length >= 2 &&
      css`
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        column-gap: 25px;

        > * {
          padding: 20px 0 15px 0 !important;
        }
      `};

    ${({ isImage, length }) =>
      isImage &&
      length >= 3 &&
      css`
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        column-gap: 20px;

        > * {
          padding: 25px 0 15px 0 !important;
        }
      `};

    ${({ isImage, length }) =>
      isImage &&
      length >= 4 &&
      css`
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        column-gap: 20px;

        > * {
          padding: 25px 0 15px 0 !important;
        }
      `};
  }
`;

const AnswerItem = styled.li<{ isVoted: boolean }>`
  position: relative;
  padding-bottom: 15px;

  &:not(:first-child) {
    padding-top: 20px;
  }

  border-bottom: 1px solid #d0d0d0;

  ${({ isVoted }) =>
    isVoted &&
    css`
      pointer-events: none;
    `};

  @media (min-width: 1400px) {
    padding: 25px 0;
  }
`;

const AnswerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AnswerLabel = styled.label`
  display: flex;
  align-items: center;
`;

const AnswerCheckbox = styled(CircularCheckbox)`
  margin-right: 10px;

  @media (min-width: 1400px) {
    margin-right: 20px;
  }

  svg {
    width: 16px;
    height: 16px;

    @media (min-width: 1400px) {
      width: 22px;
      height: 22px;
    }
  }
`;

const AnswerLabelText = styled.span`
  font-weight: 300;
  font-size: 18px;
  line-height: 140%;
  color: ${(props) => props.theme.black100};

  @media (min-width: 1400px) {
    font-size: 20px;
  }
`;

const AnswerPercent = styled.span`
  font-weight: 300;
  font-size: 18px;
  line-height: 140%;
  color: ${(props) => props.theme.black100};

  @media (min-width: 1400px) {
    font-size: 20px;
  }
`;

const StyledProgress = styled(Progress)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  transform: translateY(50%);

  height: 4px;

  @media (min-width: 1400px) {
    height: 5px;
  }

  > div {
    height: 4px;

    @media (min-width: 1400px) {
      height: 5px;
    }
  }
`;
